(() => {
  'use strict';
  if (window.PKLQuantityLimits) return;
  const rawFetch = window.fetch.bind(window);
  const root = window.Shopify?.routes?.root || '/';
  const products = new Map();
  const variants = new Map();
  const lookups = new Map();
  const seen = new WeakSet();
  const buttonState = new WeakMap();
  let cart = { items: [] };
  let queue = Promise.resolve();
  let pending = 0;
  let checkoutPending = false;
  let refreshTimer;
  let verifiedCart;
  let verifiedAt = 0;
  let messages = {};
  const text = (key, values = {}) => Object.entries(values).reduce((s, [k, v]) => s.replaceAll(`__PKL_${k.toUpperCase()}__`, String(v)).replaceAll(`{{ ${k} }}`, String(v)), messages[key] || '');
  const quantity = value => Number(value);
  const register = data => {
    if (!data || !Number.isSafeInteger(Number(data.productId))) throw new Error('Invalid product limit data');
    data.cap = Math.max(0, Number(data.cap) || 0);
    products.set(String(data.productId), data);
    for (const variant of data.variants || []) variants.set(String(variant.id), { product: data, variant });
    return data;
  };
  function scan() {
    const strings = document.getElementById('pkl-limit-messages');
    if (strings) {
      try { messages = JSON.parse(strings.dataset.pklJson || strings.textContent); } catch (_) {}
    }
    document.querySelectorAll('script[data-pkl-limit]').forEach(node => {
      if (seen.has(node)) return;
      try { register(JSON.parse(node.dataset.pklJson || node.textContent)); seen.add(node); } catch (_) {}
    });
  }
  function showError(message, scope) {
    if (scope) {
      const wrapper = scope.closest('.product-form') || scope;
      let error = wrapper.querySelector('[data-pkl-limit-error]');
      if (!error) {
        error = document.createElement('div');
        error.dataset.pklLimitError = '';
        error.className = 'pkl-limit-error';
        error.setAttribute('role', 'alert');
        wrapper.append(error);
      }
      error.textContent = message;
    }
    let notice = document.getElementById('pkl-limit-notice');
    if (!notice) {
      notice = document.createElement('div');
      notice.id = 'pkl-limit-notice';
      notice.className = 'pkl-limit-notice';
      notice.setAttribute('role', 'alert');
      document.body.append(notice);
    }
    notice.textContent = message;
    notice.hidden = false;
    clearTimeout(notice._timer);
    notice._timer = setTimeout(() => { notice.hidden = true; }, 8000);
  }
  const sum = (items, productId) => items.reduce((n, item) => n + (String(item.product_id) === String(productId) ? Number(item.quantity) : 0), 0);
  const variantSum = (items, variantId) => items.reduce((n, item) => n + (String(item.variant_id) === String(variantId) ? Number(item.quantity) : 0), 0);
  function violations(items) {
    const errors = [];
    for (const product of products.values()) {
      if (product.cap > 0 && sum(items, product.productId) > product.cap) errors.push(product);
    }
    return errors;
  }
  const limitMessage = product => text('limit', { limit: product.cap, product: product.title });
  function syncUI() {
    scan();
    const hasLimitedItems = cart.items.some(item => products.get(String(item.product_id))?.cap > 0);
    document.body?.classList.toggle('pkl-cart-has-limits', hasLimitedItems);
    document.querySelectorAll('form[action]').forEach(form => {
      if (!/\/cart\/add(?:\.js)?$/.test(new URL(form.action, location.href).pathname)) return;
      const id = form.querySelector('[name="id"]')?.value;
      const entry = variants.get(String(id));
      if (!entry?.product.cap) return;
      const rule = entry.variant.quantity_rule || { min: 1, increment: 1, max: null };
      const total = sum(cart.items, entry.product.productId);
      let remaining = Math.max(0, entry.product.cap - total);
      if (rule.max != null) remaining = Math.min(remaining, Math.max(0, Number(rule.max) - variantSum(cart.items, id)));
      const input = form.querySelector('[name="quantity"]') || document.querySelector(`input[name="quantity"][form="${CSS.escape(form.id)}"]`);
      if (input) {
        input.disabled = entry.variant.available === false || remaining < Number(rule.min || 1);
        input.min = String(rule.min || 1);
        input.max = String(remaining);
        input.dataset.pklRemaining = String(remaining);
        if (input.dataset.max !== undefined) input.dataset.max = String(entry.product.cap);
        if (remaining > 0 && Number(input.value) > remaining) input.value = String(remaining);
        if (Number(input.value) < Number(rule.min || 1)) input.value = String(rule.min || 1);
        input.closest('quantity-input')?.querySelector('button[name="plus"]')?.toggleAttribute('disabled', remaining <= Number(input.value));
      }
      const button = form.querySelector('[type="submit"][name="add"]');
      if (!button) return;
      let original = buttonState.get(button);
      if (!original) {
        const variantUnavailable = entry.variant.available === false;
        original = { disabled: variantUnavailable, label: button.querySelector('span')?.textContent || '', ariaLabel: button.getAttribute('aria-label'), card: button.classList.contains('product-card__atc'), owned: false };
        buttonState.set(button, original);
      }
      const reached = remaining < Number(rule.min || 1) && !original.disabled;
      if (original.card) {
        if (reached && !button.querySelector('.pkl-limit-check')) {
          const check = document.createElement('span');
          check.className = 'pkl-limit-check';
          check.setAttribute('aria-hidden', 'true');
          check.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" focusable="false"><path d="m5 12 4 4L19 6"/></svg>';
          button.append(check);
        }
        button.classList.toggle('pkl-limit-reached', reached);
        if (reached) button.setAttribute('aria-label', text('reached'));
        else if (original.ariaLabel !== null) button.setAttribute('aria-label', original.ariaLabel);
        else button.removeAttribute('aria-label');
      }
      const blocked = pending > 0 || remaining < Number(rule.min || 1);
      if (blocked) {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        original.owned = true;
        if (reached && !original.card) {
          const label = button.querySelector('span');
          if (label) label.textContent = text('reached');
        }
      } else if (original.owned || button.dataset.hasCap === 'true') {
        button.disabled = entry.variant.available === false;
        if (!button.disabled) button.removeAttribute('aria-disabled');
        original.owned = false;
        const label = button.querySelector('span');
        if (label && !button.disabled && !original.card) label.textContent = form.dataset.pklAddLabel || text('add');
      }
    });
    document.querySelectorAll('input[name="updates[]"][data-index]').forEach(input => {
      const item = cart.items[Number(input.dataset.index) - 1];
      const product = item && products.get(String(item.product_id));
      if (!product?.cap) return;
      let max = Math.max(0, product.cap - sum(cart.items, product.productId) + Number(item.quantity));
      const rule = variants.get(String(item.variant_id))?.variant.quantity_rule;
      if (rule?.max != null) max = Math.min(max, Number(rule.max) - variantSum(cart.items, item.variant_id) + Number(item.quantity));
      input.max = String(Math.max(0, max));
      input.closest('quantity-input')?.querySelector('button[name="plus"]')?.toggleAttribute('disabled', Number(input.value) >= max);
    });
  }
  async function readCart() {
    const response = await rawFetch(`${root}cart.js`, { credentials: 'same-origin', cache: 'no-store' });
    if (!response.ok) throw new Error('Cart unavailable');
    const value = await response.json();
    if (!Array.isArray(value.items)) throw new Error('Invalid cart');
    cart = value;
    return value;
  }
  async function loadProduct(item) {
    const cached = products.get(String(item.product_id));
    if (cached) return cached;
    if (!item.handle) throw new Error('Missing product handle');
    if (!lookups.has(item.handle)) {
      const lookup = (async () => {
        const url = `${root}products/${encodeURIComponent(item.handle)}?section_id=pkl-quantity-limit-data`;
        const response = await rawFetch(url, { credentials: 'same-origin', cache: 'no-store' });
        if (!response.ok) throw new Error('Product limit unavailable');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const node = html.querySelector('script[data-pkl-limit]');
        if (!node) throw new Error('Missing product limit');
        const data = register(JSON.parse(node.dataset.pklJson || node.textContent));
        if (String(data.productId) !== String(item.product_id)) throw new Error('Product mismatch');
        return data;
      })();
      lookups.set(item.handle, lookup);
      lookup.catch(() => lookups.delete(item.handle));
    }
    return lookups.get(item.handle);
  }
  async function loadCartProducts(value) {
    await Promise.all(value.items.map(loadProduct));
  }
  async function parsePayload(request) {
    const copy = request.clone();
    if ((copy.headers.get('content-type') || '').includes('application/json')) return copy.json();
    const data = await copy.formData();
    const value = Object.fromEntries(data);
    const updates = {};
    for (const [key, item] of data) {
      const match = key.match(/^updates\[(.+)\]$/);
      if (match) updates[match[1]] = item;
    }
    if (Object.keys(updates).length) value.updates = updates;
    if (data.has('updates[]')) value.updates = data.getAll('updates[]');
    return value;
  }
  function proposedCart(before, action, payload) {
    const items = before.items.map(item => ({ ...item }));
    if (action === 'clear') return [];
    if (action === 'add') {
      const additions = payload.items || [{ id: payload.id, quantity: payload.quantity ?? 1 }];
      if (!Array.isArray(additions)) return null;
      for (const addition of additions) {
        const entry = variants.get(String(addition.id));
        if (!entry) return null;
        const qty = quantity(addition.quantity ?? 1);
        if (!Number.isSafeInteger(qty) || qty < 0) throw new Error('Invalid quantity');
        items.push({ product_id: entry.product.productId, variant_id: Number(addition.id), quantity: qty });
      }
    } else if (action === 'change') {
      const index = payload.line ? Number(payload.line) - 1 : items.findIndex(item => item.key === String(payload.id) || String(item.variant_id) === String(payload.id));
      if (index < 0 || index >= items.length) return null;
      const qty = quantity(payload.quantity);
      if (!Number.isSafeInteger(qty) || qty < 0) throw new Error('Invalid quantity');
      items[index].quantity = qty;
    } else if (action === 'update') {
      if (payload.updates === undefined) return items;
      for (const [key, value] of Object.entries(payload.updates)) {
        const index = Array.isArray(payload.updates) ? Number(key) : items.findIndex(item => item.key === key || String(item.variant_id) === key);
        if (index < 0 || index >= items.length) return null;
        const qty = quantity(value);
        if (!Number.isSafeInteger(qty) || qty < 0) throw new Error('Invalid quantity');
        items[index].quantity = qty;
      }
    }
    return items;
  }
  function errorResponse(message) {
    showError(message);
    return new Response(JSON.stringify({ status: 422, message, description: message, errors: message }), { status: 422, headers: { 'Content-Type': 'application/json' } });
  }
  function publish(value) {
    const pubsub = window.FoxTheme?.pubsub;
    if (pubsub) pubsub.publish(pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: value });
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: value } }));
  }
  function sectionIds(requested) {
    const sections = Array.isArray(requested) ? [...requested] : String(requested || '').split(',');
    document.documentElement.dispatchEvent(new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } }));
    return [...new Set(sections.filter(Boolean))].slice(0, 5);
  }
  async function attachSections(value, bundled, requested) {
    const ids = sectionIds(requested);
    value.sections = { ...(bundled || {}) };
    const missing = ids.filter(id => typeof value.sections[id] !== 'string');
    if (missing.length) {
      const url = new URL(location.pathname, location.origin);
      url.searchParams.set('sections', missing.join(','));
      const response = await rawFetch(url, { credentials: 'same-origin', cache: 'no-store' });
      if (!response.ok) throw new Error('Cart sections unavailable');
      Object.assign(value.sections, await response.json());
    }
    return value;
  }
  async function correctCart(value, before, sections) {
    const allowances = new Map();
    const updates = {};
    const oldKeys = new Set(before.items.map(item => item.key));
    const ordered = [...value.items].sort((a, b) => Number(oldKeys.has(b.key)) - Number(oldKeys.has(a.key)));
    for (const item of ordered) {
      const product = products.get(String(item.product_id));
      if (!product?.cap) continue;
      const remaining = allowances.has(String(item.product_id)) ? allowances.get(String(item.product_id)) : product.cap;
      const allowed = Math.min(Number(item.quantity), remaining);
      allowances.set(String(item.product_id), remaining - allowed);
      if (allowed !== Number(item.quantity)) updates[item.key] = allowed;
    }
    if (!Object.keys(updates).length) return value;
    const response = await rawFetch(`${root}cart/update.js`, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates, sections: sectionIds(sections).join(','), sections_url: location.pathname }) });
    if (!response.ok) throw new Error('Cart correction failed');
    const updated = await response.json();
    const checked = await readCart();
    await loadCartProducts(checked);
    if (violations(checked.items).length) throw new Error('Cart remains over limit');
    await attachSections(checked, updated.sections, sections);
    publish(checked);
    return checked;
  }
  async function rollbackIncreases(before, sections) {
    const current = await readCart();
    const old = new Map(before.items.map(item => [item.key, item.quantity]));
    const updates = {};
    for (const item of current.items) {
      const previous = Number(old.get(item.key) || 0);
      if (item.quantity > previous) updates[item.key] = previous;
    }
    if (!Object.keys(updates).length) return;
    const response = await rawFetch(`${root}cart/update.js`, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates, sections: sectionIds(sections).join(','), sections_url: location.pathname }) });
    if (!response.ok) throw new Error('Rollback failed');
    const value = await response.json();
    await readCart();
    await attachSections(cart, value.sections, sections);
    publish(cart);
  }
  async function mutate(request, action) {
    let before;
    let payload;
    let attempted = false;
    try {
      verifiedCart = null;
      scan();
      payload = await parsePayload(request);
      before = await readCart();
      await loadCartProducts(before);
      const proposed = proposedCart(before, action, payload);
      const invalid = proposed && violations(proposed);
      if (invalid?.length) return errorResponse(limitMessage(invalid[0]));
      if (request.signal.aborted) throw new DOMException('Aborted', 'AbortError');
      attempted = true;
      const response = await rawFetch(request);
      if (!response.ok) { await readCart(); return response; }
      const after = await readCart();
      await loadCartProducts(after);
      const exceeded = violations(after.items);
      if (exceeded.length) {
        await correctCart(after, before, payload.sections);
        return errorResponse(limitMessage(exceeded[0]));
      }
      const result = await response.clone().json();
      try {
        await attachSections(after, result.sections, payload.sections);
        publish(after);
      } catch (error) {
        console.warn('Cart display update failed', error);
        document.querySelectorAll('cart-count').forEach(counter => {
          counter.textContent = String(after.item_count);
          counter.hidden = after.item_count === 0;
        });
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: action === 'add' } }));
      }
      verifiedCart = after;
      verifiedAt = Date.now();
      return response;
    } catch (error) {
      if (attempted && before) {
        try { await rollbackIncreases(before, payload?.sections); } catch (_) {}
      }
      if (error.name === 'AbortError') throw error;
      return errorResponse(text('retry'));
    } finally {
      syncUI();
    }
  }
  function serialize(operation) {
    pending++;
    syncUI();
    const result = queue.then(operation);
    queue = result.catch(() => {}).finally(() => { pending--; syncUI(); });
    return result;
  }
  window.fetch = function(input, init) {
    const url = new URL(input instanceof Request ? input.url : input, location.href);
    const method = String(init?.method || (input instanceof Request ? input.method : 'GET')).toUpperCase();
    const headers = new Headers(init?.headers || (input instanceof Request ? input.headers : undefined));
    const body = init?.body ?? (input instanceof Request ? input.body : null);
    if (url.origin === location.origin && /\/cart\/?$/.test(url.pathname) && method === 'POST' && body == null && headers.get('Accept')?.includes('application/json')) {
      if (verifiedCart && Date.now() - verifiedAt < 1000) return Promise.resolve(new Response(JSON.stringify(verifiedCart), { headers: { 'Content-Type': 'application/json' } }));
      return rawFetch(`${root}cart.js`, { credentials: 'same-origin', cache: 'no-store', signal: init?.signal });
    }
    const action = url.origin === location.origin && url.pathname.match(/\/cart\/(add|change|update|clear)(?:\.js|\.json)?\/?$/)?.[1];
    if (!action || method === 'GET' || method === 'HEAD') return rawFetch(input, init);
    const request = new Request(input instanceof Request ? input : url, init);
    return serialize(() => mutate(request, action));
  };
  const xhrOpen = XMLHttpRequest.prototype.open;
  const xhrSend = XMLHttpRequest.prototype.send;
  const xhrHeader = XMLHttpRequest.prototype.setRequestHeader;
  const xhrAbort = XMLHttpRequest.prototype.abort;
  XMLHttpRequest.prototype.open = function(method, url, async = true, ...rest) {
    this._pklRequest = { method, url: new URL(url, location.href).href, async, headers: {} };
    return xhrOpen.call(this, method, url, async, ...rest);
  };
  XMLHttpRequest.prototype.setRequestHeader = function(name, value) {
    if (this._pklRequest) this._pklRequest.headers[name] = value;
    return xhrHeader.call(this, name, value);
  };
  XMLHttpRequest.prototype.send = function(body) {
    const info = this._pklRequest;
    const url = info && new URL(info.url);
    const guarded = info && info.async && info.method.toUpperCase() !== 'GET' && url.origin === location.origin && /\/cart\/(add|change|update|clear)(?:\.js|\.json)?\/?$/.test(url.pathname);
    if (!guarded) return xhrSend.call(this, body);
    const controller = new AbortController();
    this._pklAbort = controller;
    const timeout = this.timeout > 0 ? setTimeout(() => controller.abort(), this.timeout) : null;
    this.dispatchEvent(new ProgressEvent('loadstart'));
    window.fetch(info.url, { method: info.method, headers: info.headers, body, credentials: 'same-origin', signal: controller.signal }).then(async response => {
      const content = await response.text();
      Object.defineProperties(this, { readyState: { configurable: true, value: 4 }, status: { configurable: true, value: response.status }, statusText: { configurable: true, value: response.statusText }, responseText: { configurable: true, value: content }, response: { configurable: true, value: this.responseType === 'json' ? JSON.parse(content) : content }, responseURL: { configurable: true, value: info.url } });
      this.getResponseHeader = name => response.headers.get(name);
      this.getAllResponseHeaders = () => [...response.headers].map(([k, v]) => `${k}: ${v}\r\n`).join('');
      this.dispatchEvent(new Event('readystatechange'));
      this.dispatchEvent(new ProgressEvent('load'));
    }).catch(error => this.dispatchEvent(new ProgressEvent(error.name === 'AbortError' ? 'abort' : 'error'))).finally(() => { clearTimeout(timeout); this._pklAbort = null; this.dispatchEvent(new ProgressEvent('loadend')); });
  };
  XMLHttpRequest.prototype.abort = function() {
    this._pklAbort?.abort();
    return xhrAbort.call(this);
  };
  async function checkCheckout(proceed) {
    if (checkoutPending) return;
    checkoutPending = true;
    try {
      await queue;
      await serialize(async () => {
        const value = await readCart();
        await loadCartProducts(value);
        const invalid = violations(value.items);
        if (invalid.length) {
          await correctCart(value, value, 'cart-drawer');
          showError(text('corrected', { limit: invalid[0].cap, product: invalid[0].title }));
          return;
        }
        proceed();
      });
    } catch (_) { showError(text('retry')); }
    finally { checkoutPending = false; }
  }
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const action = new URL(form.action, location.href).pathname;
    if (event.submitter?.name === 'checkout' || /\/checkout\/?$/.test(action)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      checkCheckout(() => { location.assign(`${root}checkout`); });
      return;
    }
    if (/\/cart\/?$/.test(action) && form.querySelector('[name="updates[]"]')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      window.fetch(`${root}cart/update.js`, { method: 'POST', body: new FormData(form) }).then(async response => {
        if (response.ok) location.assign(`${root}cart`);
        else showError((await response.json()).description || text('retry'), form);
      }).catch(() => showError(text('retry'), form));
      return;
    }
    if (!/\/cart\/add(?:\.js)?$/.test(action)) return;
    const entry = variants.get(String(form.querySelector('[name="id"]')?.value));
    const qty = Number(form.querySelector('[name="quantity"]')?.value || 1);
    if (entry?.product.cap && (pending > 0 || sum(cart.items, entry.product.productId) + qty > entry.product.cap)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (!pending) showError(limitMessage(entry.product), form);
      return;
    }
    const nativeForm = form.getAttribute('is') !== 'product-form' || window.FoxTheme?.settings?.cartType === 'page' || document.body.classList.contains('cart-template');
    if (nativeForm) {
      event.preventDefault();
      event.stopImmediatePropagation();
      window.fetch(`${root}cart/add.js`, { method: 'POST', body: new FormData(form) }).then(async response => {
        if (response.ok) location.assign(`${root}cart`);
        else showError((await response.json()).description || text('retry'), form);
      }).catch(() => showError(text('retry'), form));
    }
  }, true);
  document.addEventListener('click', event => {
    const target = event.target instanceof Element && event.target.closest('a[href], [name="checkout"], [data-pkl-checkout]');
    if (!target) return;
    const isCheckoutLink = target.matches('a[href]') && /\/checkout\/?$/.test(new URL(target.href, location.href).pathname);
    if (!isCheckoutLink && !target.matches('[name="checkout"], [data-pkl-checkout]')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    checkCheckout(() => { location.assign(isCheckoutLink ? target.href : `${root}checkout`); });
  }, true);
  const refresh = () => {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => {
      if (pending) return;
      readCart().then(loadCartProducts).then(syncUI).catch(() => {});
    }, 100);
  };
  const init = () => {
    scan();
    const initial = document.getElementById('pkl-limit-cart');
    if (initial) { try { cart = JSON.parse(initial.dataset.pklJson || initial.textContent); } catch (_) {} }
    syncUI();
    const observer = new MutationObserver(records => {
      if (records.some(record => [...record.addedNodes].some(node => node.nodeType === 1))) syncUI();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    document.addEventListener('change', event => { if (event.target.matches('[name="id"], [name="quantity"], [name^="options"]')) setTimeout(syncUI, 0); });
    ['cart:updated', 'product-ajax:added', 'quick-view:open', 'shopify:section:load'].forEach(name => document.addEventListener(name, refresh));
    const pubsub = window.FoxTheme?.pubsub;
    if (pubsub) pubsub.subscribe(pubsub.PUB_SUB_EVENTS.cartUpdate, data => { if (data?.cart?.items) { cart = data.cart; syncUI(); } else refresh(); });
    window.addEventListener('focus', refresh);
    window.addEventListener('pageshow', refresh);
    refresh();
  };
  window.PKLQuantityLimits = Object.freeze({ refresh });
  if (document.readyState !== 'complete') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
